import { useState, useLayoutEffect, useMemo, useRef } from 'react';
import { BordFeedbackAlert } from 'bord-design-system';
import { getNudosData } from '../../../../../../../utils/getNudosData';
import SelectableToolCard from 'components/NewSelectableToolCard/SelectableToolCard';
import NudosDatePicker from 'components/NudosComponents/NudosDatePicker/NudosDatePicker';
import useDeliveredToolsState, { SECTIONS_STEPS } from '../../../../../../../state/useDeliveredToolsState';
import { BordOneToneIcon, NudosButton } from '../../../../../../../components/NudosComponents';
import { IlogisticServiceDetails, IlogisticServiceTrelloCardData } from '../../../../../../../types/services';
import { updateDeliveryStatus, updateStatusService } from '../../../../../../../services/services';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../../../utils/displayNudosStandardNotifications';
import {
  extractUndeliveredProductIds,
  buildMarkAsDeliveredPayload
} from '../../../../../../../utils/logisticServiceOriginDestination.utils';
import { LogisticsOriginAndDestinationInfo } from '../LogisticsOriginAndDestinationInfo/LogisticsOriginAndDestinationInfo';
import type { OffboardingLateralChrome } from '../../offboardingLateralModal.types';
import { bordColorTheme } from '../../../../../../../styles/styles.constants';

import './ConfirmationSection.scss';

interface IConfirmationLabels {
  unselectedToolsTitle: string;
  unselectedAlertText: string;
  selectedToolsTitle: string;
  headerTitle: string;
  nextButtonText: string;
}

interface IConfirmationSectionProps {
  closeModalCallback: () => void;
  serviceDetails: IlogisticServiceDetails;
  closeModalAfterSubmitCallback: () => void;
  isUpdateTools?: boolean;
  cardData?: IlogisticServiceTrelloCardData;
  onLateralChromeChange?: (chrome: OffboardingLateralChrome | null) => void;
}


const ConfirmationSection = ({
  closeModalCallback,
  serviceDetails,
  closeModalAfterSubmitCallback,
  isUpdateTools = false,
  cardData,
  onLateralChromeChange
}: IConfirmationSectionProps) => {
  const usesLateralChrome = Boolean(onLateralChromeChange);

  const {
    deliveryDate,
    setCurrentStep,
    setDeliveryDate,
    getUnselectedToolsArray,
    getSelectedToolsArray,
    markedNotDeliveredToolIds
  } = useDeliveredToolsState();

  const [isProcessing, setIsProcessing] = useState(false);

  const selectedTools = getSelectedToolsArray();
  const unselectedTools = getUnselectedToolsArray();

  const handleUpdateUndeliveredTools = async (): Promise<void> => {
    if (!serviceDetails?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: Faltan datos del servicio logístico</>
      });
      return;
    }

    const undeliveredProductIds = extractUndeliveredProductIds(unselectedTools);

    try {
      setIsProcessing(true);
      await updateDeliveryStatus({
        logisticServiceId: serviceDetails.id,
        notDeliveredProductIds: undeliveredProductIds
      });

      displaySuccessNotification({
        customJSXMessage: <>Herramientas actualizadas exitosamente</>
      });

      closeModalAfterSubmitCallback();
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar el estado de entrega de las herramientas</>
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMarkServiceAsDelivered = async (): Promise<void> => {
    if (!serviceDetails?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: Faltan datos del servicio logístico</>
      });
      return;
    }

    if (!deliveryDate) {
      displayErrorNotification({
        customJSXMessage: <>Debes seleccionar una fecha de entrega</>
      });
      return;
    }

    const nudosUser = getNudosData();
    if (!nudosUser?.userData?.id) {
      displayErrorNotification({
        customJSXMessage: <>Error: No se pudo obtener la información del usuario</>
      });
      return;
    }

    const undeliveredProductIds = extractUndeliveredProductIds(unselectedTools);
    const statusUpdatePayload = buildMarkAsDeliveredPayload(nudosUser.userData.id, deliveryDate, [
      ...undeliveredProductIds,
      ...markedNotDeliveredToolIds
    ]);

    try {
      setIsProcessing(true);
      await updateStatusService(serviceDetails.id, statusUpdatePayload);

      displaySuccessNotification({
        customJSXMessage: <>Servicio logístico actualizado exitosamente</>
      });

      closeModalAfterSubmitCallback();
    } catch {
      displayErrorNotification({
        customJSXMessage: <>Error al actualizar el estado del servicio logístico</>
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = async (): Promise<void> => {
    if (isUpdateTools) {
      await handleUpdateUndeliveredTools();
    } else {
      await handleMarkServiceAsDelivered();
    }
  };

  const handleBack = () => {
    setCurrentStep(SECTIONS_STEPS.SELECTION);
  };

  const handleConfirmRef = useRef(handleConfirm);
  const handleBackRef = useRef(handleBack);
  handleConfirmRef.current = handleConfirm;
  handleBackRef.current = handleBack;

  const labels = useMemo((): IConfirmationLabels => {
    if (isUpdateTools) {
      return {
        unselectedToolsTitle: 'Herramientas que no serán entregadas',
        unselectedAlertText:
          'Estas herramientas no fueron entregadas y permanecerán en su ubicación anterior',
        selectedToolsTitle: 'Herramientas que serán entregadas',
        headerTitle: 'Actualizar entrega',
        nextButtonText: 'Actualizar herramientas'
      };
    }
    return {
      unselectedToolsTitle: 'Herramientas que no fueron entregadas',
      unselectedAlertText:
        'Estas herramientas no fueron entregadas y permanecerán en su ubicación anterior.',
      selectedToolsTitle: 'Herramientas que fueron entregadas',
      headerTitle: 'Pasar a entregado',
      nextButtonText: 'Pasar a entregado'
    };
  }, [isUpdateTools]);

  useLayoutEffect(() => {
    if (!onLateralChromeChange) {
      return undefined;
    }

    const isFooterPrimaryDisabled = isUpdateTools ? isProcessing : !deliveryDate || isProcessing;

    onLateralChromeChange({
      headerProps: {
        title: labels.headerTitle,
        subtitle: 'Revisa y confirma las herramientas antes de continuar',
        subtitleClassName: 'text-xss text-nudos-gray-500 font-normal',
        leading: (
          <span className="confirmationSectionHeaderLeading">
            <BordOneToneIcon
              variant={isUpdateTools ? 'refreshCw' : 'truck'}
              customWidth="2.4rem"
              stroke={bordColorTheme.stroke.default}
            />
          </span>
        ),
        showCloseButton: true,
        onClose: closeModalCallback
      },
      footerProps: {
        firstButton: {
          label: 'Regresar',
          modeButton: 'secondary',
          customWidth: 'w-full',
          onClick: () => handleBackRef.current()
        },
        secondButton: {
          label: labels.nextButtonText,
          modeButton: 'primary',
          customWidth: 'w-full',
          onClick: () => void handleConfirmRef.current(),
          disabled: isFooterPrimaryDisabled || isProcessing,
        }
      }
    });

    return undefined;
  }, [
    onLateralChromeChange,
    closeModalCallback,
    isUpdateTools,
    deliveryDate,
    isProcessing,
    labels.headerTitle,
    labels.nextButtonText
  ]);

  return (
    <div className="confirmationSection">
      {cardData ? <LogisticsOriginAndDestinationInfo cardData={cardData} /> : null}
      {!isUpdateTools && (
        <div className="datePickerContainer">
          <NudosDatePicker
            componentSize="w-216"
            label="Fecha de entrega (obligatorio)"
            updateCurrentValueCallback={setDeliveryDate}
            isFilled={!!deliveryDate}
            defaultValue={deliveryDate}
            desiredTimeZone={-6}
            isDeactivated
          />
        </div>
      )}
      <div className="toolHeaderDivider" />
      <div className="toolsContainer">
        {unselectedTools?.length > 0 && (
          <>
            <div className="unselectedToolsHeader">
              <BordOneToneIcon variant="xCircle" standardSize={18} stroke={bordColorTheme.red[700]} />
              <span className="unselectedToolsHeaderText">
                {labels.unselectedToolsTitle} ({unselectedTools.length})
              </span>
            </div>

            <BordFeedbackAlert
              type="warning"
              className="confirmationSectionFeedbackAlert"
              title={labels.unselectedAlertText}
            />

            {unselectedTools.map(({ tool, originalIndex }) => (
              <SelectableToolCard
                key={`unselected-${originalIndex}`}
                tool={tool}
                showCheckbox={false}
                customClassName="borderRed confirmationSectionToolCard"
                isUnselected
              />
            ))}
          </>
        )}

        {selectedTools?.length > 0 ? (
          <div className="selectedToolsHeader">
            <span className="selectedToolsHeaderText">
              {labels.selectedToolsTitle} ({selectedTools.length})
            </span>
          </div>
        ) : null}

        {selectedTools.map(({ tool, originalIndex }) => (
          <SelectableToolCard
            key={`selected-${originalIndex}`}
            tool={tool}
            showCheckbox={false}
            customClassName="borderGray confirmationSectionToolCard"
          />
        ))}
      </div>
    </div>
  );
};

export default ConfirmationSection;
